import axios from "axios";
import { busToast } from "@/utils/toastBus";
import { enqueueFailedRequest, bindAutoSync, flushQueue } from "@/utils/syncQueue";
import { cacheGetResponse, lookupCachedResponse, listCachedResponses } from "@/utils/responseCache";
import { loadCachedData, CACHE_KEYS } from "@/utils/offlineCache";
import { cacheScope } from "@/utils/cacheScope";

// Admin product list / search while the server is down: the exact URL+params may
// never have been cached, but the POS cache holds EVERY product → filter locally.
const _norm = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
async function _productsFromPosCache(cfg) {
  const url = (cfg.url || "").replace(API, "");
  if (url !== "/products" && !url.startsWith("/products?")) return null;
  if (!cacheScope(cfg).startsWith("t:")) return null; // POS product pool belongs to a tenant, never to the super admin
  const params = cfg.params || {};
  let list = await loadCachedData(CACHE_KEYS.PRODUCTS, []);
  if (!list.length) {
    // No POS cache on this PC (admin-only machine) → union every cached admin page into one local pool
    const pages = await listCachedResponses(`${API}/products`, cfg);
    const seen = new Map();
    for (const pg of pages) for (const p of (pg.data?.items || (Array.isArray(pg.data) ? pg.data : []))) if (p && p.id && !seen.has(p.id)) seen.set(p.id, p);
    list = [...seen.values()];
  }
  if (!list.length) return null;
  const words = _norm(params.search).split(/\s+/).filter(Boolean);
  if (params.category_id) list = list.filter((p) => p.category_id === params.category_id);
  if (params.status) list = list.filter((p) => (p.status || "active") === params.status);
  if (params.out_of_stock) list = list.filter((p) => !(Number(p.stock) > 0));
  if (params.brand) list = list.filter((p) => _norm(p.brand) === _norm(params.brand));
  if (words.length) list = list.filter((p) => { const hay = _norm(p.name) + " " + _norm(p.code) + " " + _norm(p.barcode) + " " + (p.barcodes || []).map(_norm).join(" "); return words.every((w) => hay.includes(w)); });
  const total = list.length;
  const skip = Number(params.skip) || 0, limit = Number(params.limit) || total;
  return { items: list.slice(skip, skip + limit), total, offline_cache: true };
}

// Always use relative path - works in both preview and production
const BACKEND_URL = "";
const API = "/api";

// ⏱️ Hard timeout on every request. Without this, a "connected but dead"
// network (WiFi up, ISP down) hangs requests for minutes and freezes the POS.
axios.defaults.timeout = 10000;

// Heavy list endpoints get a longer leash on slow Venezuelan links —
// a big (but healthy) download must not be misread as "network down".
const HEAVY_GET_PATTERNS = ["/products", "/sales-orders", "/customers", "/inventory", "/dashboard/stats", "/sales-report"];
axios.interceptors.request.use((config) => {
  try { config.headers["X-App-Lang"] = localStorage.getItem("app_lang") || "es"; } catch { /* ignore */ }
  const m = (config.method || "get").toLowerCase();
  if (m === "get" && (config.timeout === undefined || config.timeout === 10000)) {
    const u = config.url || "";
    if (HEAVY_GET_PATTERNS.some((p) => u.includes(p))) config.timeout = 25000;
  }
  return config;
});

// ---- Global network error handling ----
// Goal: never leave the user guessing why a click did nothing, BUT don't spam
// the screen on flaky networks.
//
// Strategy:
//  • GET requests (background data loading): auto-retry 3× with exponential
//    backoff and STAY SILENT. The calling component's own loading state is
//    enough feedback. If every retry fails we just reject — the page shows
//    its own empty / "failed to load" state.
//  • Mutations (POST/PUT/PATCH/DELETE): retry 2× then surface a toast with a
//    visible "Reintentar / 重试" button — user needs to know their change
//    didn't land.
//  • Auth errors (401/403) stay silent (router handles redirect).
//  • Only ONE network-error toast visible at a time globally (`id: ...`).

const GET_RETRY_LIMIT = 3;
const MUTATION_RETRY_LIMIT = 2;
const RETRY_BACKOFF_MS = 600;      // base; doubled each attempt

function _isNetworkError(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") return true;
  if (!error.response) return true;                    // no response at all
  const s = error.response.status;
  return s >= 500 && s <= 599;
}

function _isMutation(method) {
  const m = (method || "get").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
}

const GLOBAL_TOAST_ID = "net-error";
let _lastToastAt = 0;

// ---- 🛰️ Server-down circuit breaker (2026-09-29 Kamatera outage) ----
// Internet fine + sellox.shop unreachable = every request hangs to its timeout,
// then retries… the boss waited minutes per page. Once ONE request fails AND a
// 6s /api/health probe confirms the server is dead, the circuit opens: for the
// next 30s every request fails instantly → GETs answer from cache, mutations go
// to the offline queue with zero lag. A 10s background probe closes it again.
let _circuitOpenUntil = 0;
let _probeTimer = null;
let _probeInFlight = null;
let _episodeToastShown = false; // one outage banner per episode, not every 60s
export const isServerDown = () => Date.now() < _circuitOpenUntil;
async function _probeHealth() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000); // 10 s: VE→US links often crawl for a few seconds without being "down"
    const r = await fetch(`${API}/health?cb=1`, { signal: ctrl.signal, cache: "no-store" });
    clearTimeout(t);
    return r.ok;
  } catch { return false; }
}
function _openCircuit() {
  _circuitOpenUntil = Date.now() + 30000;
  if (_probeTimer) return;
  _probeTimer = setInterval(async () => {
    if (await _probeHealth()) {
      _circuitOpenUntil = 0;
      clearInterval(_probeTimer); _probeTimer = null;
      const wasShown = _episodeToastShown; _episodeToastShown = false;
      if (wasShown) busToast("success", "✅ 已重新连上 / Conexión recuperada", { id: GLOBAL_TOAST_ID, description: "离线保存的操作正在自动上传 / Subiendo lo guardado offline", duration: 3500 });
      flushQueue(axios).catch(() => {});
    } else {
      _circuitOpenUntil = Date.now() + 30000;
    }
  }, 10000);
}
// One probe at a time; concurrent failures share the same verdict.
async function _confirmServerDown() {
  if (isServerDown()) return true;
  if (!_probeInFlight) _probeInFlight = _probeHealth().finally(() => { _probeInFlight = null; });
  const ok = await _probeInFlight;
  if (!ok) _openCircuit();
  return !ok;
}
// ---- 📡 Who is down: Sellox or the store's internet? (2026-10-02) ----
// The browser's navigator.onLine only knows the Wi-Fi link is up, not that the
// internet works. On flaky Venezuelan links /health times out too, and the old
// red "Sellox server down (your internet is fine)" banner made tenants blame
// Sellox. Now we also probe independent hosts: them reachable + us not = server;
// nothing reachable = their internet. Verdict cached 30s.
let _outageVerdict = { kind: null, at: 0 };
export async function probeInternet(ms = 6000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    await Promise.any(["https://www.gstatic.com/generate_204", "https://cloudflare.com/cdn-cgi/trace"]
      .map((u) => fetch(`${u}?cb=${Date.now()}`, { mode: "no-cors", cache: "no-store", signal: ctrl.signal })));
    return true;
  } catch { return false; } finally { clearTimeout(t); }
}
export async function outageKind() {
  if (Date.now() - _outageVerdict.at < 30000 && _outageVerdict.kind) return _outageVerdict.kind;
  const kind = (navigator.onLine && await probeInternet()) ? "server" : "internet";
  _outageVerdict = { kind, at: Date.now() };
  return kind;
}
export const OUTAGE_TOAST = {
  server: { title: "📡 暂时连不上 Sellox 服务器 / No se alcanza el servidor Sellox", description: "多半是国际线路拥堵，几分钟内会自动恢复。已切换离线模式：可继续收银，改价/进货先存本机，恢复后自动上传 / Suele ser congestión de internet internacional; se recupera solo. Modo offline: puedes seguir cobrando" },
  internet: { title: "📡 你店里的网络不稳 / Tu internet está fallando (Sellox está bien)", description: "已切换离线模式：可继续收银，显示本机缓存，网络恢复后自动上传 / Modo offline: puedes seguir cobrando, se sube al volver tu internet" },
};
export async function outageToast(duration = 8000) {
  const k = await outageKind();
  busToast("warning", OUTAGE_TOAST[k].title, { id: GLOBAL_TOAST_ID, description: OUTAGE_TOAST[k].description, duration });
  return k;
}
const _realAdapter = axios.getAdapter(axios.defaults.adapter);
const _alwaysTry = (url) => /\/auth\/(login|tenant-login|pos-login|me)\b|\/health\b/.test(String(url || ""));
axios.defaults.adapter = (config) => {
  // login / health always go to the wire: a stale open circuit must never lock the boss out of the login page
  if (isServerDown() && !config.__bypassCircuit && !_alwaysTry(config.url)) {
    return Promise.reject(new axios.AxiosError("Sellox server unreachable (circuit open)", "ERR_NETWORK", config));
  }
  return _realAdapter(config);
};

function _showRetryToast(config, error) {
  const now = Date.now();
  // Cooldown: one toast per 5 seconds globally
  if (now - _lastToastAt < 5000) return;
  _lastToastAt = now;
  const isOffline = !navigator.onLine;
  const title = isOffline
    ? "Sin conexión a internet / 网络断开"
    : "No se pudo guardar / 保存失败";
  busToast("error", title, {
    id: GLOBAL_TOAST_ID,
    description: (error?.response?.status ? `HTTP ${error.response.status}` : "Timeout")
      + (config?.url ? `  ·  ${config.url.replace(API, "")}` : ""),
    duration: 6000,
    action: {
      label: "🔄 Reintentar / 重试",
      onClick: () => {
        const cfg = { ...config };
        if (cfg.__retryCount !== undefined) delete cfg.__retryCount;
        axios(cfg).catch(() => { /* re-surfaced by interceptor */ });
      },
    },
  });
}

axios.interceptors.response.use(
  (response) => {
    // any real answer from the server proves it is alive → close the circuit immediately (don't wait for the 10 s probe)
    if (_circuitOpenUntil) { _circuitOpenUntil = 0; if (_probeTimer) { clearInterval(_probeTimer); _probeTimer = null; } _episodeToastShown = false; flushQueue(axios).catch(() => {}); }
    // 🔄 Sliding session renewal: backend sends a fresh token when <6h life remains
    try {
      const renewed = response?.headers?.["x-renewed-token"];
      // Read-only support tab must keep its read-only identity — never adopt a renewed owner token here
      if (renewed && !sessionStorage.getItem("impersonate_readonly")) {
        if (localStorage.getItem("pos_token")) localStorage.setItem("pos_token", renewed);
        if (localStorage.getItem("token")) localStorage.setItem("token", renewed);
        // Admin panel keeps its per-tab identity in sessionStorage — must renew
        // there too, otherwise a tab restore resurrects the OLD (expired) token.
        if (sessionStorage.getItem("token")) sessionStorage.setItem("token", renewed);
        axios.defaults.headers.common["Authorization"] = `Bearer ${renewed}`;
      }
    } catch {}
    // 🌐 Desktop LAN host answered a write while the cloud is down
    try {
      const cfg = response?.config;
      if (cfg && _isMutation(cfg.method) && response?.data && response.data.lan === true && !response.data.duplicate) {
        const now = Date.now();
        if (now - _lastToastAt > 1500) {
          _lastToastAt = now;
          busToast("success", "💾 已保存到店内主机 / Guardado en el equipo principal", {
            id: GLOBAL_TOAST_ID, description: "服务器恢复后自动上传 / Se sube al volver internet", duration: 3500,
          });
        }
      }
    } catch {}
    // Mirror successful GETs into the universal cache. Failures fall back to
    // this in the rejection handler below.
    try {
      const cfg = response?.config;
      if (cfg && (cfg.method || "get").toLowerCase() === "get") {
        // Don't await — caching is best-effort, must not stall the response
        cacheGetResponse(cfg, response).catch(() => {});
      }
    } catch {}
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    // 🔒 super-admin read-only view: server refused a write
    if (status === 403 && error?.response?.data?.readonly) {
      busToast("error", "🔒 只读查看模式，不能修改租户数据 / Solo lectura", { id: "readonly-403", duration: 4000 });
      return Promise.reject(error);
    }
    // Single-device kick: another device logged into this cashier account
    if (status === 401 && String(error?.response?.data?.detail || "").startsWith("SESSION_KICKED")) {
      busToast("error", "⚠️ 账号已在其他设备登录，本机已退出 / Sesión iniciada en otro equipo", { id: "session-kicked", duration: 8000 });
      try { localStorage.removeItem("pos_token"); localStorage.removeItem("token"); } catch {}
      setTimeout(() => { window.location.href = "/login"; }, 1800);
      return Promise.reject(error);
    }
    // Expired/invalid JWT inside the ADMIN panel: don't leave the boss on a
    // dead session where every "save" fails with a cryptic "Token expired" —
    // clear the stale token and send them back to login with a clear message.
    // POS / public / shop routes keep their silent 401 handling (offline flows).
    if (status === 401 && typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      const det = String(error?.response?.data?.detail || "");
      if (det === "Token expired" || det === "Invalid token") {
        busToast("error", "🔒 登录已过期，请重新登录 / Sesión expirada, inicia sesión de nuevo", { id: "session-expired", duration: 6000 });
        try {
          sessionStorage.removeItem("token"); sessionStorage.removeItem("cached_auth_user");
          localStorage.removeItem("token"); localStorage.removeItem("cached_auth_user");
        } catch {}
        delete axios.defaults.headers.common["Authorization"];
        setTimeout(() => { window.location.href = "/login"; }, 1600);
        return Promise.reject(error);
      }
    }
    // Silent for auth errors (handled by router)
    if (status === 401 || status === 403) {
      console.warn("Auth error:", status);
      return Promise.reject(error);
    }

    const cfg = error?.config;
    // Explicit opt-out: `config.skipGlobalError = true`
    if (!cfg || cfg.skipGlobalError) return Promise.reject(error);

    if (_isNetworkError(error)) {
      const isMut = _isMutation(cfg.method);
      // Desktop proxy signals "definitely offline" — skip retries, go straight
      // to cache/queue fallback (was: 3 retries × timeouts = frozen UI).
      const proxyOffline = error?.response?.status === 502 &&
        (error?.response?.headers?.["x-sellox-proxy"] === "offline" ||
         error?.response?.data?.detail === "offline");
      // Browser says we're offline — retrying only freezes the UI. Go straight
      // to cache (GET) / queue (mutations) for an instant, lag-free response.
      const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      // Server dead while our internet is fine → same instant fallback as browser-offline
      const trueNetFail = !error?.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
      const serverDown = !proxyOffline && !browserOffline && trueNetFail && (await _confirmServerDown());
      const limit = (proxyOffline || browserOffline || serverDown) ? 0 : (isMut ? MUTATION_RETRY_LIMIT : GET_RETRY_LIMIT);
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= limit) {
        // Exponential backoff: 600, 1200, 2400 ms
        const delay = RETRY_BACKOFF_MS * Math.pow(2, cfg.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        return axios(cfg);
      }
      // All retries exhausted.
      // GET requests: fall back to the universal response cache so the page
      // shows last-known data (rather than an empty skeleton or error).
      // Mutations: persist to the universal offline queue so they auto-replay
      // when the network returns — "fire and forget" UX. If queueing succeeds,
      // suppress the retry toast (we'll show a friendlier "saved offline" one).
      if (isMut) {
        if (!cfg.__isReplay) {
          const queued = await enqueueFailedRequest(cfg, error);
          if (queued) {
            // Single, calmer toast — user knows their click DID land
            const now = Date.now();
            if (now - _lastToastAt > 1500) {
              _lastToastAt = now;
              busToast("success", "💾 Guardado offline / 已离线保存", {
                id: GLOBAL_TOAST_ID,
                description: "Se subirá automáticamente al recuperar internet / 网络恢复后自动上传",
                duration: 3500,
              });
            }
            // Return a synthetic resolved response so caller code doesn't crash
            return Promise.resolve({
              status: 202,
              data: { ok: true, queued: true, offline: true },
              headers: {},
              config: cfg,
            });
          }
        }
        _showRetryToast(cfg, error);
      } else {
        // GET fallback — return cached response if available
        try {
          const cached = await lookupCachedResponse(cfg);
          if (cached) {
            // Synthetic 200 from cache. Caller code is none the wiser.
            console.info("[net] serving cached:", cfg.url);
            // Only warn when it's a REAL network failure (no response at all —
            // 5xx means the server answered, network is fine) AND the cached
            // copy is meaningfully stale (>60s). Fresh cache = data is current,
            // no need to alarm the cashier. Cooldown raised to 60s to stop the
            // banner from nagging on flaky Venezuelan links.
            const cacheAgeSec = Math.floor((Date.now() - cached.cached_at) / 1000);
            const trueNetErr = !error?.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
            const nowTs = Date.now();
            if (trueNetErr && cacheAgeSec > 60 && nowTs - _lastToastAt > 60000) {
              _lastToastAt = nowTs;
              if (isServerDown() && !_episodeToastShown) {
                _episodeToastShown = true;
                outageToast(8000).catch(() => {});
              } else if (!isServerDown()) {
                busToast("warning", "📡 网络不稳 / Red inestable", {
                  id: GLOBAL_TOAST_ID,
                  description: "当前显示的是本机缓存数据，可能不是最新 / Mostrando datos guardados localmente",
                  duration: 5000,
                });
              }
            }
            return Promise.resolve({
              status: 200,
              data: cached.data,
              headers: { "x-sellox-cache": "hit", "x-sellox-cache-age": String(cacheAgeSec) },
              config: cfg,
            });
          }
        } catch {}
        // Admin product list: synthesize from the POS product cache
        try {
          const fromPos = await _productsFromPosCache(cfg);
          if (fromPos) return Promise.resolve({ status: 200, data: fromPos, headers: { "x-sellox-cache": "pos" }, config: cfg });
        } catch {}
        // No cache either — log for debugging, let caller handle empty
        console.warn("[net] GET failed after retries:", cfg.url, error?.code || error?.response?.status);
      }
    }
    return Promise.reject(error);
  }
);

// ---- Online / offline banner ----
// Venezuelan networks flap constantly (drop 1-2s then recover). Debounce:
// only show the red banner if we stay offline > 3.5s, and only show the
// green "restored" toast if the red banner was actually shown.
if (typeof window !== "undefined") {
  let _offlineTimer = null;
  let _bannerShown = false;
  window.addEventListener("offline", () => {
    clearTimeout(_offlineTimer);
    _offlineTimer = setTimeout(() => {
      if (navigator.onLine) return; // recovered during grace period — stay silent
      _bannerShown = true;
      busToast("error", "🔴 Sin internet / 网络已断开", {
        id: "offline-banner",
        duration: Infinity,
        description: "Los cambios se guardarán cuando vuelvas a conectar.",
      });
    }, 3500);
  });
  window.addEventListener("online", () => {
    clearTimeout(_offlineTimer);
    if (_bannerShown) {
      _bannerShown = false;
      busToast("dismiss", "offline-banner");
      busToast("success", "🟢 Internet restaurado / 网络已恢复", { duration: 2500 });
    }
  });
}

// Bind universal offline-queue auto-sync — replays everything queued on
// `online` and once on app start.
bindAutoSync(axios);

/**
 * Returns the canonical public origin for customer-facing share links / QR codes.
 *
 * Was a fleet-wide bug: every QR (payment verify, receipt, shop QR poster,
 * loyalty signup, layaway, points referral...) used `window.location.origin`,
 * which on a kiosk loaded via local IP (`http://192.168.1.x:3000`) or
 * `localhost` produced QR targets that customer phones (esp. on cellular
 * data) could never reach. They scanned, the page wouldn't open.
 *
 * 2026-05-14: harder fix. Some PWA installs cached an OLD JS bundle built
 * with REACT_APP_BACKEND_URL=https://backup.sellox.shop (which now hosts the
 * B2C ecommerce, NOT the POS). Hardcode the public POS domain so QRs always
 * resolve, regardless of which build the PWA still has cached.
 */
const PUBLIC_POS_DOMAIN = "https://sellox.shop";
export function getPublicOrigin() {
  // Production POS lives on sellox.shop — never on backup.*, the desktop
  // app's internal 127.0.0.1:38173 server, or window.location.
  // ONLY real local dev (localhost:3000) falls back to window.location.origin.
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    const port = window.location.port || "";
    if ((host === "localhost" || host === "127.0.0.1") && port === "3000") {
      return window.location.origin;
    }
  }
  return PUBLIC_POS_DOMAIN;
}

export { API, BACKEND_URL };
export default axios;
